import { getUseResource, resolveResourceUrl } from '../resource';
import { authResources } from '../builders/authResources';
import { IResponse } from '../models/IResponse';
import { IBasicUserInfo, IUserInfo } from '../models/IUserInfo';
import { useContext } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import { UUID } from '../../types/uuid';
import { IMfaDevice } from '../../models/IMfaDevice';

type ISignUpRequest = Pick<IUserInfo, 'first_name' | 'last_name' | 'email' | 'phone_number'> & { password: string };

interface ITokenResponse {
  access_token: string;
  refresh_token: string;
  expires_at: string;
  mfa_devices?: IMfaDevice[];
  mfa_message?: string;
}

interface IRegister2FAResponse {
  qr: string;
  backup_code: string;
  device_id: UUID;
}

interface IRegister2FARequest {
  mfa_method: EMfaMethod;
  name: string;
  is_default: boolean;
}

interface IRegister2FAByMethodIdRequest {
  method_id: UUID;
  name: string;
  is_default: boolean;
}

interface I2FAMethod {
  id: UUID;
  name: EMfaMethod;
  enable: boolean;
  allow_resend: boolean;
}

export enum EMfaMethod {
  Email = 'email',
  AuthApp = 'auth_app',
}

export const apiAuth = {
  useSignUp: getUseResource(
    authResources.signUp,
    (body): IResponse<IBasicUserInfo> => body,
    (params, payload: ISignUpRequest) => payload,
    void 0,
    void 0,
    {
      addAuthHeader: false,
    }
  ),
  useLogin: getUseResource(
    authResources.login,
    (body): IResponse<ITokenResponse> => body,
    (params, email: string, password: string) => ({ email, password }),
    void 0,
    void 0,
    {
      addAuthHeader: false,
    }
  ),
  useGet2FAMethods: getUseResource(authResources.get2faMethods, (body): IResponse<I2FAMethod[]> => body),
  useRegister2fa: getUseResource(
    authResources.register2fa,
    (body): IResponse<IRegister2FAResponse> => body,
    (params, payload: IRegister2FARequest) => payload
  ),
  useRegister2faByMethodId: getUseResource(
    authResources.register2fa,
    (body): IResponse<IRegister2FAResponse> => body,
    (params, payload: IRegister2FAByMethodIdRequest) => payload,
    (resource, apiExecutor) => {
      const resourceUrl = resource ? resolveResourceUrl(resource) : null;
      return async ({ method_id, ...payload }): Promise<IResponse<IRegister2FAResponse>> => {
        if (!resourceUrl) {
          throw new Error('resource is missing');
        }

        const response = await apiExecutor.post<IResponse<IRegister2FAResponse>>(resourceUrl, payload, {
          params: { method_id },
        });

        return response.data;
      };
    }
  ),
  useVerify2faSetup: getUseResource(
    authResources.verify2faSetup,
    (body): IResponse<void> => body,
    (params, device_id: UUID, token: string) => ({ device_id, token })
  ),
  useVerify2fa: getUseResource(
    authResources.verify2fa,
    (body): IResponse<ITokenResponse> => body,
    (params, device_id: UUID, token: string) => ({ device_id, token }),
    (resource, apiExecutor) => {
      const resourceUrl = resource ? resolveResourceUrl(resource) : null;
      const { auth2faCache } = useContext(AuthContext);
      return async (payload): Promise<IResponse<ITokenResponse>> => {
        if (!resourceUrl) {
          throw new Error('resource is missing');
        }

        const response = await apiExecutor.post<IResponse<ITokenResponse>>(resourceUrl, payload, {
          headers: {
            Authorization: `Bearer ${auth2faCache?.access_token}`,
          },
        });
        return response.data;
      };
    },
    void 0,
    { addAuthHeader: false }
  ),
  useSend2fa: getUseResource(
    authResources.send2fa,
    (body): IResponse<void> => body,
    (params, device_id: UUID) => ({ device_id }),
    (resource, apiExecutor) => {
      const resourceUrl = resource ? resolveResourceUrl(resource) : null;
      const { auth2faCache } = useContext(AuthContext);
      return async (payload): Promise<IResponse<void>> => {
        if (!resourceUrl) {
          throw new Error('resource is missing');
        }
        const response = await apiExecutor.post<IResponse<void>>(resourceUrl, null, {
          headers: {
            Authorization: `Bearer ${auth2faCache?.access_token}`,
          },
          params: payload,
        });
        return response.data;
      };
    },
    void 0,
    { addAuthHeader: false }
  ),
  useUseBackup2faCode: getUseResource(
    authResources.useBackup2faCode,
    (body): IResponse<void> => body,
    (params, device_id: UUID, code: string) => ({ device_id, code })
  ),
  useInitiateDisable2fa: getUseResource(
    authResources.initiateDisable2fa,
    (body): IResponse<{ message: string }> => body,
    (params, device_id: UUID) => ({ device_id }),
    (resource, apiExecutor) => {
      const resourceUrl = resource ? resolveResourceUrl(resource) : null;
      return async ({ device_id }): Promise<IResponse<{ message: string }>> => {
        if (!resourceUrl) {
          throw new Error('resource is missing');
        }
        const response = await apiExecutor.post<IResponse<{ message: string }>>(resourceUrl, null, {
          params: { device_id },
        });
        return response.data;
      };
    }
  ),
  useConfirmDisable2fa: getUseResource(
    authResources.confirmDisable2fa,
    (body): IResponse<{ message: string }> => body,
    (params, device_id: UUID, token: string) => ({ device_id, token })
  ),
  useLogout: getUseResource(authResources.logout, (body): IResponse<void> => body),
  useLogoutFromAllSessions: getUseResource(authResources.logoutFromAllSessions, (body): IResponse<void> => body),
  useRefreshToken: getUseResource(authResources.refreshToken, (body): IResponse<ITokenResponse> => body),
  useSendEmailVerificationCode: getUseResource(
    authResources.sendEmailVerificationCode,
    (body): IResponse<void> => body
  ),
  useVerifyEmailByToken: getUseResource(
    authResources.verifyEmailByToken,
    (body): IResponse<void> => body,
    (params, code: string) => ({ code }),
    void 0,
    void 0,
    { addAuthHeader: false }
  ),
  useChangePassword: getUseResource(
    authResources.changePassword,
    (body): IResponse<any> => body,
    (params, payload: { old_password: string; new_password: string }) => payload
  ),
  useRequestPasswordReset: getUseResource(
    authResources.requestPasswordReset,
    (body): IResponse<IResponse<void>> => body,
    (params, email: string) => ({ email }),
    (resource, apiExecutor) => (payload) =>
      apiExecutor.post<IResponse<void>>(resource!.template, void 0, { params: payload }).then(({ data }) => data)
  ),
  useConfirmPasswordReset: getUseResource(
    authResources.confirmPasswordReset,
    (body): IResponse<void> => body,
    (params, token: string, password: string) => ({ token, password }),
    (resource, apiExecutor) => (payload) =>
      apiExecutor
        .post(resource!.template, { password: payload.password }, { params: { token: payload.token } })
        .then(({ data }) => data)
  ),
  useZendeskSso: getUseResource(authResources.zendeskSso, (body): IResponse<{ token: string }> => body),
};
